import { useState } from "react"
import type { TotalizadorGroup } from "@/types/analise-previdenciario"
import { formatCurrency, formatCpf, formatPeriodo, formatDateTime, isTotalizadorDeRetorno } from "../../../utils/previdenciario-utils"
import { CopyableText } from "./copyable-text"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  ChevronDown,
  ChevronRight,
  User,
  CalendarDays,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react"

interface TotalizadorPanelProps {
  group: TotalizadorGroup
}

export function TotalizadorPanel({ group }: TotalizadorPanelProps) {
  const [isOpen, setIsOpen] = useState(false)
  const toNumber = (value: string) => parseFloat(value || "0")

  // If the API provides a retorno (resposta) totalizador, use it as the source of truth
  // for the totals displayed per CPF. Otherwise, fall back to summing all totalizadores.
  const totalizadoresDeRetorno = group.totalizadores.filter((t) => isTotalizadorDeRetorno(t))
  const totalizadoresParaTotais = totalizadoresDeRetorno.length > 0 ? totalizadoresDeRetorno : group.totalizadores

  const totalValor = totalizadoresParaTotais.reduce(
    (sum, t) => sum + toNumber(t.valor),
    0
  )
  const totalValor13 = totalizadoresParaTotais.reduce(
    (sum, t) => sum + toNumber(t.valor_13),
    0
  )
  const totalCpSeg = totalizadoresParaTotais.reduce(
    (sum, t) => sum + toNumber(t.vrcpseg),
    0
  )
  const totalDescSeg = totalizadoresParaTotais.reduce(
    (sum, t) => sum + toNumber(t.vrdescseg),
    0
  )
  const getPagamentoDhProcessamento = (ideDmDev: string): string | null => {
    const recibo = group.recibos.find((r) => r.ideDmDev === ideDmDev && r.dhProcessamento)
    if (!recibo) return null
    return recibo.totalizador?.dhProcessamento || recibo.totalizador?.perApur || recibo.dhProcessamento || null
  }

  const recibosOrdenados = [...group.nrRecibos].sort()
  const resumoRecibos =
    recibosOrdenados.length === 1
      ? recibosOrdenados[0]
      : `${recibosOrdenados.length} recibos`

  const pagamentosComProcessamento = group.pagamentos.map((p) => ({
    ...p,
    dhProcessamento: getPagamentoDhProcessamento(p.ideDmDev),
  }))

  const pagamentosOrdenadosPorData = [...pagamentosComProcessamento].sort((a, b) => {
    const aTime = a.dhProcessamento ? new Date(a.dhProcessamento).getTime() : 0
    const bTime = b.dhProcessamento ? new Date(b.dhProcessamento).getTime() : 0
    return bTime - aTime
  })

  const pagamentoMaisRecente =
    pagamentosOrdenadosPorData.find((p) => p.dhProcessamento) ||
    pagamentosOrdenadosPorData[0] ||
    null

  const hasRetorno = group.totalizadores.some((t) => isTotalizadorDeRetorno(t))
  const allOriginal = group.totalizadores.every((t) => !isTotalizadorDeRetorno(t))
  const retornos = group.totalizadores.filter((t) => isTotalizadorDeRetorno(t))
  const originais = group.totalizadores.filter((t) => !isTotalizadorDeRetorno(t))
  const hasMultipleTotalizadores = group.totalizadores.length > 1
  const shouldValidateRetificacao =
    hasMultipleTotalizadores &&
    retornos.length > 0 &&
    originais.length > 0

  const originalValor13 = originais.reduce((sum, t) => sum + toNumber(t.valor_13), 0)
  const retificadoValor13 = retornos.reduce((sum, t) => sum + toNumber(t.valor_13), 0)
  const originalValor = originais.reduce((sum, t) => sum + toNumber(t.valor), 0)
  const retificadoValor = retornos.reduce((sum, t) => sum + toNumber(t.valor), 0)
  const diferencaValor = originalValor - retificadoValor
  const diferencaValor13 = originalValor13 - retificadoValor13
  const totalizadorDiferenca = diferencaValor + diferencaValor13
  const totalizadorExibicao = hasMultipleTotalizadores ? totalizadorDiferenca : 0
  const diferencaValorExibicao = hasMultipleTotalizadores ? diferencaValor : 0
  const diferencaValor13Exibicao = hasMultipleTotalizadores ? diferencaValor13 : 0
  const originalCpSeg = originais.reduce((sum, t) => sum + toNumber(t.vrcpseg), 0)
  const retificadoCpSeg = retornos.reduce((sum, t) => sum + toNumber(t.vrcpseg), 0)

  const retificadoMaiorValor = retificadoValor > originalValor
  const retificadoMaiorValor13 = retificadoValor13 > originalValor13
  const retificadoMaiorCpSeg = retificadoCpSeg > originalCpSeg
  const invalidFields = [
    retificadoMaiorValor ? "Valor" : null,
    retificadoMaiorValor13 ? "Valor 13o" : null,
    retificadoMaiorCpSeg ? "CP Seg" : null,
  ].filter(Boolean) as string[]
  const retificacaoInvalida =
    shouldValidateRetificacao &&
    (retificadoMaiorValor || retificadoMaiorValor13 || retificadoMaiorCpSeg)
  const retificacaoOk =
    shouldValidateRetificacao && !retificacaoInvalida

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="transition-all hover:shadow-md">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer select-none">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                {isOpen ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                )}
                <div>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <CalendarDays className="h-4 w-4 text-chart-1" />
                    <span className="capitalize">
                      {formatPeriodo(group.perApur)}
                    </span>
                  </CardTitle>
                  <div className="flex items-center gap-1.5 mt-1">
                    <User className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">CPF:</span>
                    <CopyableText
                      text={group.cpfTrabalhador}
                      displayText={formatCpf(group.cpfTrabalhador)}
                      className="text-xs"
                    />
                  </div>
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className="text-xs text-muted-foreground">Recibos:</span>
                    {recibosOrdenados.length === 1 ? (
                      <CopyableText
                        text={resumoRecibos}
                        className="text-xs"
                      />
                    ) : (
                      <span className="text-xs text-foreground">{resumoRecibos}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {hasRetorno ? (
                  <Badge
                    variant="destructive"
                    className="text-xs flex items-center gap-1"
                  >
                    <AlertTriangle className="h-3 w-3" />
                    Retorno
                  </Badge>
                ) : allOriginal ? (
                  <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 text-xs flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    Original
                  </Badge>
                ) : null}
                <Badge variant="outline" className="text-xs">
                  {group.totalizadores.length} totalizador(es)
                </Badge>
              </div>
            </div>

            {/* Summary metrics */}
            <div className="grid grid-cols-2 gap-2 mt-3 md:grid-cols-5">
              <div className="rounded-lg bg-muted/50 px-3 py-2">
                <p className="text-[10px] text-muted-foreground uppercase">
                  Valor Total
                </p>
                <p className="text-sm font-semibold">
                  {formatCurrency(totalValor)}
                </p>
              </div>
              <div className="rounded-lg bg-muted/50 px-3 py-2">
                <p className="text-[10px] text-muted-foreground uppercase">
                  13o Salario
                </p>
                <p className="text-sm font-semibold">
                  {formatCurrency(totalValor13)}
                </p>
              </div>
              <div className="rounded-lg bg-muted/50 px-3 py-2">
                <p className="text-[10px] text-muted-foreground uppercase">
                  CP Segurado
                </p>
                <p className="text-sm font-semibold">
                  {formatCurrency(totalCpSeg)}
                </p>
              </div>
              <div className="rounded-lg bg-muted/50 px-3 py-2">
                <p className="text-[10px] text-muted-foreground uppercase">
                  Desc Segurado
                </p>
                <p className="text-sm font-semibold">
                  {formatCurrency(totalDescSeg)}
                </p>
              </div>
              <div className="rounded-lg bg-muted/50 px-3 py-2">
                <p className="text-[10px] text-muted-foreground uppercase">
                  Totalizador
                </p>
                <p className="text-sm font-semibold">
                  {formatCurrency(totalizadorExibicao)}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  (Valor {formatCurrency(diferencaValorExibicao)} + 13o {formatCurrency(diferencaValor13Exibicao)})
                </p>
              </div>
            </div>

            {shouldValidateRetificacao && (
              <div
                className={`mt-3 rounded-lg border px-3 py-2 text-xs ${
                  retificacaoInvalida
                    ? "border-destructive/40 bg-destructive/10 text-destructive"
                    : "border-emerald-300/50 bg-emerald-50 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300"
                }`}
              >
                <div className="flex items-center gap-1.5 font-semibold">
                  {retificacaoInvalida ? (
                    <>
                      <AlertTriangle className="h-3.5 w-3.5" />
                      Retificacao inconsistente ({invalidFields.join(" + ")}): desfazer retificacao.
                    </>
                  ) : retificacaoOk ? (
                    <>
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Retificacao OK para Valor, Valor 13o e CP Seg.
                    </>
                  ) : null}
                </div>
                <div className="mt-1 grid grid-cols-1 gap-1 md:grid-cols-3">
                  <span>
                    Valor Original/Retorno: {formatCurrency(originalValor)} / {formatCurrency(retificadoValor)}
                  </span>
                  <span>
                    Valor 13o Original/Retorno: {formatCurrency(originalValor13)} / {formatCurrency(retificadoValor13)}
                  </span>
                  <span>
                    CP Seg Original/Retorno: {formatCurrency(originalCpSeg)} / {formatCurrency(retificadoCpSeg)}
                  </span>
                </div>
              </div>
            )}
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="space-y-4 pt-0">
            {/* Detailed totalizadores */}
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                Detalhamento dos Totalizadores
              </h4>
              <div className="rounded-md border overflow-hidden">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="text-xs min-w-[260px]">
                          Nr Recibo
                        </TableHead>
                        <TableHead className="text-xs">TPCR</TableHead>
                        <TableHead className="text-xs text-right">
                          Valor
                        </TableHead>
                        <TableHead className="text-xs text-right">
                          13o
                        </TableHead>
                        <TableHead className="text-xs text-right">
                          CP Seg
                        </TableHead>
                        <TableHead className="text-xs text-right">
                          Desc Seg
                        </TableHead>
                        <TableHead className="text-xs text-center">
                          Status
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {group.totalizadores.map((t, i) => (
                        <TableRow key={`${t.nrRecibo}-${i}`}>
                          <TableCell>
                            <CopyableText
                              text={t.nrRecibo}
                              className="text-[11px]"
                            />
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {t.tpcr}
                          </TableCell>
                          <TableCell className="text-right font-semibold text-xs">
                            {formatCurrency(t.valor)}
                          </TableCell>
                          <TableCell className="text-right text-xs">
                            {formatCurrency(t.valor_13)}
                          </TableCell>
                          <TableCell className="text-right text-xs">
                            {formatCurrency(t.vrcpseg)}
                          </TableCell>
                          <TableCell className="text-right text-xs">
                            {formatCurrency(t.vrdescseg)}
                          </TableCell>
                          <TableCell className="text-center">
                            {isTotalizadorDeRetorno(t) ? (
                              <Badge
                                variant="destructive"
                                className="text-[10px]"
                              >
                                Retorno
                              </Badge>
                            ) : (
                              <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 text-[10px]">
                                Original
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>

            {/* Pagamentos linked */}
            {group.pagamentos.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  Pagamentos relacionados
                </h4>
                <div className="rounded-md border overflow-hidden">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead className="text-xs min-w-[260px]">
                            Nr Recibo Pgto
                          </TableHead>
                          <TableHead className="text-xs">ideDmDev</TableHead>
                          <TableHead className="text-xs">
                            Dh Processamento
                          </TableHead>
                          <TableHead className="text-xs text-right">
                            Valor Liquido
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pagamentosComProcessamento.map((p, i) => (
                          <TableRow key={`${p.nrRecibo}-${i}`}>
                            <TableCell>
                              <CopyableText
                                text={p.nrRecibo}
                                className="text-[11px]"
                              />
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              {p.ideDmDev}
                            </TableCell>
                            <TableCell className="text-xs">
                              {p.dhProcessamento
                                ? formatDateTime(p.dhProcessamento)
                                : "Data/hora indisponivel"}
                            </TableCell>
                            <TableCell className="text-right font-semibold text-xs">
                              {formatCurrency(p.vrLiq)}
                            </TableCell>
                          </TableRow>
                        ))}
                        {pagamentoMaisRecente && (
                          <TableRow className="bg-muted/30 font-semibold">
                            <TableCell colSpan={3} className="text-xs">
                              Total (mais recente)
                            </TableCell>
                            <TableCell className="text-right text-xs">
                              {formatCurrency(pagamentoMaisRecente.vrLiq)}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}
